import { useEffect, useRef, useState } from "react";
import BaseCard from "../../../components/ui/BaseCard";
import { useMutation, useQuery } from "@tanstack/react-query";
import BaseButton from "../../../components/ui/BaseButton";
import BaseDataTableV2 from "../../../components/ui/BaseDataTableV2";
import BaseDropdownV2 from "../../../components/ui/BaseDropdownV2";

import { useCurrentUser } from "../../../hooks/useCurrentUser";
import CompleteProfileModal from "../../WorkJobDetails/CompleteProfileModal";
import useAuth from "../../../hooks/useAuth";
import AddEditCompanyBranchModal from "./components/AddEditCompanyBranchModal";
import { deleteBranch, fetchCompanyBranches } from "../../../services/api/branch";
import BaseConfirmModal from "../../../components/ui/BaseConfirmModal";
import { showToast } from "../../../store/toast/toastSlice";
import { useDispatch } from "react-redux";
import BaseTextField from "../../../components/ui/BaseTextField";
import { debounce } from "lodash";

import { formatPhoneNumber } from "../../../utils/helpers";
const menuOptions = [
  {
    label: "Edit",
    value: "EDIT",
  },

  {
    label: "Delete",
    value: "DELETE",
  },
];

const CompanyBranchesTable = () => {
  const [isAddEditCompanyBranchModalOpen, setIsAddEditCompanyBranchModalOpen] = useState(false);
  const dispatch = useDispatch();
  const [companyBranchesList, setCompanyBranchesList] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const { userData } = useCurrentUser();
  const [isProfileCompleted, setIsProfileCompleted] = useState(false);
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false);
  const { authRole } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");

  const baseQueryParams = {
    limit: 20,
    page: 1,
    search: "",
  };
  const [queryParams, setQueryParams] = useState(baseQueryParams);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [rowCount, setRowCount] = useState(0);

  const {
    isPending: isDeleteBranchApiLoading,
    error: isDeleteBranchApiError,
    data: deleteBranchData,
    mutateAsync: deleteBranchMutation,
  } = useMutation({
    mutationFn: deleteBranch,
    enabled: false,
  });

  const {
    data: branchesData,
    isPending: isFetchCompanyBranchesApiLoading,
    error: isFetchCompanyBranchesApiError,
    refetch,
  } = useQuery({
    queryKey: ["branchesData", queryParams.page, queryParams.limit, queryParams.search],
    queryFn: () =>
      fetchCompanyBranches({
        page: queryParams.page,
        limit: queryParams.limit,
        search: queryParams.search,
      }),
  });
  useEffect(() => {
    if (branchesData?.data?.data) {
      setCompanyBranchesList(branchesData.data?.data);
      setRowCount(branchesData.data.totalCount);
    }
  }, [branchesData]);

  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.5,
      minWidth: 80,
      renderCell: (params) => (
        <p className="text-primary cursor-pointer" onClick={() => handleBranchView(params?.row)}>
          {params?.value}
        </p>
      ),
    },
    {
      field: "name",
      headerName: "Branch Name",
      flex: 0.5,
      minWidth: 150,
      renderCell: (params) => (
        <p className="text-primary cursor-pointer" onClick={() => handleBranchView(params?.row)}>
          {params.value}
        </p>
      ),
    },
    {
      field: "taxType",
      headerName: "Tax Type",
      flex: 0.3,
      minWidth: 150,
      renderCell: (params) => <p>{params.value}</p>,
    },
    {
      field: "taxNumber",
      headerName: "Tax Number",
      flex: 0.5,
      minWidth: 150,
      renderCell: (params) => <p>{params.value}</p>,
    },
    {
      field: "phone",
      headerName: "Phone Number",
      flex: 0.5,
      minWidth: 150,
      renderCell: (params) => <p>{formatPhoneNumber(params?.value)}</p>,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 0.5,
      minWidth: 220,
      renderCell: (params) => <p>{params?.value}</p>,
    },
    {
      field: "addressLine1",
      headerName: "Address",
      flex: 0.5,
      minWidth: 150,
      renderCell: (params) => <p>{params?.row?.address?.addressLine1}</p>,
    },
    {
      field: "country",
      headerName: "Country",
      flex: 0.5,
      minWidth: 150,
      renderCell: (params) => <p>{params?.row?.address?.country}</p>,
    },
    {
      field: "state",
      headerName: "State",
      flex: 0.5,
      minWidth: 150,
      renderCell: (params) => <p>{params?.row?.address?.state}</p>,
    },
    {
      field: "city",
      headerName: "City",
      flex: 0.5,
      minWidth: 150,
      renderCell: (params) => <p>{params?.row?.address?.city}</p>,
    },
    {
      field: "zipCode",
      headerName: "Zip Code",
      flex: 0.5,
      minWidth: 150,
      renderCell: (params) => <p>{params?.row?.address?.zipCode}</p>,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      minWidth: 90,
      renderCell: (params) => {
        return (
          <BaseDropdownV2
            menuOptions={menuOptions}
            triggerElement={<i className="fa-solid fa-ellipsis cursor-pointer ml-4 text-base"></i>}
            onItemClick={(item) => handleItemClick(item, params?.row)}
          />
        );
      },
    },
  ];

  const handleItemClick = async (value, data) => {
    setSelectedBranch(data);
    if (value === "EDIT") {
      setIsEditMode(true);
      setIsAddEditCompanyBranchModalOpen(true);
    } else if (value === "DELETE") {
      setIsDeleteConfirmationModalOpen(true);
    }
  };

  // Clicking the ID or name opens the branch read-only, like bank accounts.
  const handleBranchView = (branch) => {
    setSelectedBranch(branch);
    setIsEditMode(false);
    setIsAddEditCompanyBranchModalOpen(true);
  };

  const handleDeleteBranchButton = async () => {
    try {
      const deletedBranch = await deleteBranchMutation(selectedBranch?.id);
      setCompanyBranchesList((prevBranches) =>
        prevBranches.filter((b) => b.id !== deletedBranch.data.id),
      );
      setIsDeleteConfirmationModalOpen(false);
      dispatch(
        showToast({
          message: "Branch deleted successfully",
          type: "success",
        }),
      );
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "success",
        }),
      );
    }
  };
  const updateQueryParamsDebounced = useRef(
    debounce((query) => {
      setQueryParams((prev) => ({
        ...prev,
        search: query.trim() || "",
        page: 1,
      }));
    }, 500),
  ).current;
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    updateQueryParamsDebounced(query);
  };
  const handlePaginationChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);

    setQueryParams((prev) => ({
      ...prev,
      page: newPaginationModel.page + 1,
      limit: newPaginationModel.pageSize,
    }));
  };

  return (
    <>
      <BaseCard elevation={0} className="py-4 px-5 rounded-xl shadow-lg ">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 w-full">
          <div className="w-full sm:w-2/6">
            <BaseTextField
              placeholder="Search by branch name or email"
              value={searchQuery}
              onChange={handleSearchChange}
              startAdornment={<i className="fa-light fa-magnifying-glass text-lg"></i>}
            />
          </div>
          <BaseButton
            color="primary"
            className="!py-[5px] whitespace-nowrap w-full sm:w-auto"
            onClick={() => {
              if (userData?.status !== "APPROVED") {
                setIsProfileCompleted(true);
                return;
              }
              setIsAddEditCompanyBranchModalOpen(true);
              setSelectedBranch(null);
              setIsEditMode(true);
            }}
          >
            Add New Branch
          </BaseButton>
        </div>
      </BaseCard>

      <BaseDataTableV2
        className="max-h-[calc(100vh-320px)] overflow-auto"
        columns={columns}
        data={companyBranchesList}
        isLoading={isFetchCompanyBranchesApiLoading || isDeleteBranchApiLoading}
        isCellEditable={(params) => false}
        hideFooter={false}
        pagination
        rowCount={rowCount}
        pageSizeOptions={[10, 20, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
        paginationMode="server"
      />

      {isAddEditCompanyBranchModalOpen && (
        <AddEditCompanyBranchModal
          open={isAddEditCompanyBranchModalOpen}
          close={setIsAddEditCompanyBranchModalOpen}
          setCompanyBranchesList={setCompanyBranchesList}
          selectedBranch={selectedBranch}
          setSelectedBranch={setSelectedBranch}
          isEditMode={isEditMode}
          setIsEditMode={setIsEditMode}
          refetchBranchesData={refetch}
        />
      )}

      {isDeleteConfirmationModalOpen && (
        <BaseConfirmModal
          open={isDeleteConfirmationModalOpen}
          onClose={() => setIsDeleteConfirmationModalOpen(false)}
          cancelText="Cancel"
          confirmText="Delete"
          confirmButtonColor="error"
          title="Delete Branch"
          isLoading={isDeleteBranchApiLoading}
          description={
            <p>
              Are you sure you want to delete the branch
              {
                <span className="text-red-600 font-medium mx-1 capitalize">
                  "{selectedBranch?.name}"
                </span>
              }
              ? This action cannot be undone.
            </p>
          }
          onConfirm={handleDeleteBranchButton}
        />
      )}

      {isProfileCompleted && (
        <CompleteProfileModal
          isProfileCompleted={isProfileCompleted}
          setIsProfileCompleted={setIsProfileCompleted}
          status={userData?.status}
          authRole={authRole}
        />
      )}
    </>
  );
};

export default CompanyBranchesTable;
